<template>
  <v-row id="controlDetails">
    <v-col cols="12">
      <h4 class="overline mb-1">Control Details</h4>
      <v-card flat color="bg_darkerish">
        <v-card-text class="py-6 px-6" v-if="selectedControl && selectedControl.id">
          <p class="text-h6 font-weight-light white--text d-flex">
            <span class="dw_green--text mr-2">{{ selectedControl.label }}</span>
            {{ selectedControl.title }}
            <v-spacer></v-spacer>
            <v-btn
              icon
              small
              @click="$emit('share-control', selectedControl.id)"
              :title="`Share ${selectedControl.label} via PDF`"
              class="mr-1">
              <v-icon color="dw_green" class="my-auto" v-text="mdiShareVariantOutline" />
            </v-btn>
          </p>
          <p class="body-2 font-weight-light">
            <span class="white--text font-weight-regular">Control: </span>
            {{ selectedControl.statement || '--' }}
          </p>
          <p class="body-2 font-weight-light">
            <span class="white--text font-weight-regular">Discussion: </span>
            {{ selectedControl.guidance || '--' }}
          </p>
          <p class="body-2 font-weight-light" v-if="selectedControl.url">
            <a :href="selectedControl.url" target="_blank">
              <v-icon small class="my-auto mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              {{ selectedControl.url }}
            </a>
          </p>
          <v-divider class="my-2"></v-divider>

          <p class="body-2 text-capitalize font-weight-light mb-1 mr-5 white--text">
            <span class="font-weight-regular mr-1">Family:</span>
            {{ selectedControl.family.name.toLowerCase() }}
            <v-btn
              icon
              small
              v-if="filters.fam === undefined || filters.fam.id !== selectedControl.family.id"
              @click="$emit('filter-by-fam', 'add', selectedControl.family);"
              class="mb-1 ml-2"
              title="Add Filter">
              <v-icon small color="dw_green" v-text="mdiFilterVariantPlus" />
            </v-btn>
            <v-btn
              icon
              small
              v-else
              @click="$emit('filter-by-fam', 'remove', selectedControl.family);"
              class="mb-1 ml-2"
              title="Remove Filter">
              <v-icon small color="danger" v-text="mdiFilterVariantRemove" />
            </v-btn>
          </p>
          <v-divider class="my-2"></v-divider>

          <div v-if="selectedControl.parent">
            <p class="text-subtitle-1 font-weight-regular white--text mb-0">
              <span>Parent Control</span>
            </p>
            <v-chip-group column>
              <v-chip
                label
                outlined
                small
                color="dw_green"
                @click="$router.push({ name: 'control_view', params: {
                  id: selectedControl.parent
                }})">
                {{ selectedControl.parent }}
              </v-chip>
            </v-chip-group>
            <v-divider class="my-2"></v-divider>
          </div>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            <span>Related Controls</span>
          </p>
          <!-- TODO: serializer for related controls instead of string, task #102 -->
          <v-chip-group column>
            <v-chip
              v-if="relatedControls.length === 0"
              label
              outlined
              small
              color="grey">No Related Controls</v-chip>
            <v-chip
              v-else
              v-for="relCtrl in relatedControls"
              :key="`relCtrl.${relCtrl}`"
              label
              outlined
              small
              color="dw_green"
              @click="$router.push({ name: 'control_view', params: { id: relCtrl }})">
              {{ relCtrl }}
            </v-chip>
          </v-chip-group>
          <v-divider class="my-2"></v-divider>

          <div v-if="selectedControl.references">
            <p class="text-subtitle-1 font-weight-regular white--text mb-0">
              References
            </p>
            <ul class="font-weight-light">
              <li class="mb-1" v-for="ref of references" :key="ref">
                <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                {{ ref }}
              </li>
            </ul>
            <v-divider class="my-2"></v-divider>
          </div>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            <span>Control Enhancements</span>
          </p>
          <v-chip-group column>
            <v-chip
              v-if="selectedControl.enhancements.length === 0"
              label
              outlined
              small
              color="grey">No Enhancements</v-chip>
            <v-chip
              v-else
              v-for="enhmt in selectedControl.enhancements"
              :key="enhmt.sort_id"
              label
              outlined
              small
              color="dw_green"
              @click="selectEnhancement(enhmt)">
              {{ enhmt.label }}
            </v-chip>
          </v-chip-group>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            <span>Related Plays</span>
          </p>
          <v-chip-group column>
            <v-chip
              v-if="selectedControl.related_plays.length === 0"
              label
              outlined
              small
              color="grey">No Related Plays</v-chip>
            <v-tooltip
              bottom
              v-else
              v-for="ply in selectedControl.related_plays"
              :key="`ply.${ply.id}`">
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  label
                  outlined
                  small
                  color="dw_green"
                  v-bind="attrs"
                  v-on="on"
                  @click="$router.push({ name: 'play_view', params: { id: ply.id }})">
                  {{ ply.id }}
                </v-chip>
              </template>
              <span>{{ ply.name }}</span>
            </v-tooltip>
          </v-chip-group>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col cols="12" id="enhancementDetails">
      <div v-if="selectedEnhancement">
        <h4 class="overline mb-1">Control Enhancement Details</h4>
        <v-card flat color="bg_darkerish" min-height="10vh">
          <enhancement-details :subenh="selectedEnhancement" :err="enhErr" />
          <v-system-bar height="4"></v-system-bar>
          <v-card-actions>
            <v-slide-group multiple show-arrows>
              <v-slide-item
                v-for="enh in selectedControl.enhancements"
                :key="`enh.${enh.id}`"
              >
                <v-btn text @click="selectEnhancement(enh)">{{ enh.label }}</v-btn>
              </v-slide-item>
            </v-slide-group>
          </v-card-actions>
        </v-card>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import {
  mdiShareVariantOutline,
  mdiSquareMediumOutline,
  mdiFilterVariantPlus,
  mdiFilterVariantRemove
} from '@mdi/js';
import enhancementDetails from '@/components/playbook/details/enhancementDetails.vue';
import apiService from '@/services/api.service';

export default {
  name: 'controlDetails',
  props: ['filters'],
  components: {
    enhancementDetails
  },
  data() {
    return {
      mdiShareVariantOutline,
      mdiSquareMediumOutline,
      mdiFilterVariantPlus,
      mdiFilterVariantRemove,
      relatedControls: [],
      references: [],
      selectedEnhancement: undefined,
      enhErr: false
    }
  },
  watch: {
    '$route.params': {
      async handler(params) {
        if (params.id) {
          await this.fetchControlByID(params.id);
          this.selectedEnhancement = undefined;
          this.$vuetify.goTo('#controlDetails');
        }
      },
      immediate: true
    },
    selectedControl() {
      this.setRelated();
      this.setReferences();
    }
  },
  computed: {
    ...mapGetters([
      'selectedControl'
    ])
  },
  beforeRouteLeave(to, from, next) {
    if (to.name !== 'control_view') {
      this.clearSelected('control');
      this.selectedEnhancement = undefined;
    }
    next();
  },
  methods: {
    ...mapActions([
      'fetchControlByID'
    ]),
    ...mapMutations([
      'clearSelected'
    ]),
    setRelated() {
      if (this.selectedControl.related === '') this.relatedControls = [];
      else this.relatedControls = this.selectedControl.related.split(',');
    },
    setReferences() {
      const regex = /\[|\]/gi;
      let refs = this.selectedControl.references;
      refs = refs.replace(regex, '');
      this.references = refs.split(',');
    },
    async selectEnhancement(enh) {
      try {
        this.selectedEnhancement = await apiService.get(`controls/${enh.id}/`);
        this.enhErr = false;
      } catch {
        this.selectedEnhancement = { ...enh };
        this.enhErr = true;
      }
      this.$vuetify.goTo('#enhancementDetails');
    }
  }
};
</script>

<style scoped>
ul {
  padding: 0 0 0 0;
}
li {
  list-style-type: none;
}
</style>
